/**
 * Rate Limiting Service
 * Client-side request throttling and rate limiting
 */

/**
 * Rate limit configuration
 */
interface RateLimitConfig {
  /** Maximum requests allowed in the time window */
  maxRequests: number;

  /** Time window in milliseconds */
  windowMs: number;

  /** Key to identify the rate limit (e.g., endpoint, action) */
  key?: string;

  /** Callback when rate limit is exceeded */
  onRateLimitExceeded?: (key: string, retryAfter: number) => void;
}

/**
 * Rate limit entry
 */
interface RateLimitEntry {
  /** Request timestamps */
  requests: number[];

  /** Time window end */
  windowEnd: number;
}

/**
 * Rate Limiting Service
 */
export class RateLimitService {
  private limits: Map<string, RateLimitEntry> = new Map();
  private cleanupInterval: ReturnType<typeof setInterval> | null = null;

  constructor() {
    // Cleanup expired entries every minute
    this.cleanupInterval = setInterval(() => this.cleanup(), 60000);
  }

  /**
   * Check if request is allowed under rate limit
   */
  public checkLimit(config: RateLimitConfig): {
    allowed: boolean;
    retryAfter?: number;
    remaining?: number;
  } {
    const key = config.key || 'default';
    const now = Date.now();

    // Get or create rate limit entry
    let entry = this.limits.get(key);

    if (!entry || now > entry.windowEnd) {
      // Create new window
      entry = {
        requests: [now],
        windowEnd: now + config.windowMs,
      };
      this.limits.set(key, entry);

      return {
        allowed: true,
        remaining: config.maxRequests - 1,
      };
    }

    // Remove old requests outside the window
    entry.requests = entry.requests.filter(
      (timestamp) => timestamp > now - config.windowMs
    );

    // Check if limit exceeded
    if (entry.requests.length >= config.maxRequests) {
      const oldestRequest = entry.requests[0];
      const retryAfter = oldestRequest + config.windowMs - now;

      if (config.onRateLimitExceeded) {
        config.onRateLimitExceeded(key, retryAfter);
      }

      return {
        allowed: false,
        retryAfter: Math.ceil(retryAfter / 1000), // Convert to seconds
        remaining: 0,
      };
    }

    // Add current request
    entry.requests.push(now);
    this.limits.set(key, entry);

    return {
      allowed: true,
      remaining: config.maxRequests - entry.requests.length,
    };
  }

  /**
   * Reset rate limit for a key
   */
  public reset(key: string = 'default'): void {
    this.limits.delete(key);
  }

  /**
   * Cleanup expired entries
   */
  private cleanup(): void {
    const now = Date.now();

    for (const [key, entry] of this.limits.entries()) {
      if (now > entry.windowEnd) {
        this.limits.delete(key);
      }
    }
  }

  /**
   * Get current rate limit status
   */
  public getStatus(key: string = 'default'): {
    requests: number;
    limit: number;
    windowEnd?: number;
  } | null {
    const entry = this.limits.get(key);

    if (!entry) {
      return null;
    }

    const now = Date.now();
    const activeRequests = entry.requests.filter(
      (timestamp) => timestamp > now - (entry.windowEnd - entry.requests[0])
    );

    return {
      requests: activeRequests.length,
      limit: entry.requests.length,
      windowEnd: entry.windowEnd,
    };
  }

  /**
   * Destroy service and cleanup
   */
  public destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.limits.clear();
  }
}

/**
 * Throttle function execution
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limitMs: number
): (...args: Parameters<T>) => ReturnType<T> | undefined {
  let lastRun = 0;

  return function (this: any, ...args: Parameters<T>): ReturnType<T> | undefined {
    const now = Date.now();

    if (now - lastRun >= limitMs) {
      lastRun = now;
      return func.apply(this, args);
    }

    return undefined;
  };
}

/**
 * Debounce function execution
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  waitMs: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return function (this: any, ...args: Parameters<T>): void {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, waitMs);
  };
}

/**
 * Singleton rate limit service instance
 */
let rateLimitServiceInstance: RateLimitService | null = null;

/**
 * Get or create rate limit service instance
 */
export function getRateLimitService(): RateLimitService {
  if (!rateLimitServiceInstance) {
    rateLimitServiceInstance = new RateLimitService();
  }
  return rateLimitServiceInstance;
}

/**
 * Create rate limiter for specific action
 */
export function createRateLimiter(config: Omit<RateLimitConfig, 'key'>) {
  const service = getRateLimitService();

  return (key: string) => service.checkLimit({ ...config, key });
}

/**
 * Predefined rate limiters
 */
export const rateLimiters = {
  /** API requests: 60 requests per minute */
  api: createRateLimiter({
    maxRequests: 60,
    windowMs: 60000,
    onRateLimitExceeded: (key, retryAfter) => {
      console.warn(`[RateLimit] API rate limit exceeded for ${key}. Retry after ${retryAfter}s`);
    },
  }),

  /** Form submissions: 5 requests per minute */
  formSubmit: createRateLimiter({
    maxRequests: 5,
    windowMs: 60000,
    onRateLimitExceeded: (_key, retryAfter) => {
      console.warn(`[RateLimit] Form submission rate limit exceeded. Retry after ${retryAfter}s`);
    },
  }),

  /** Search: 20 requests per minute */
  search: createRateLimiter({
    maxRequests: 20,
    windowMs: 60000,
  }),

  /** reCAPTCHA: 10 requests per hour */
  recaptcha: createRateLimiter({
    maxRequests: 10,
    windowMs: 3600000,
  }),
};
